/* eslint-disable consistent-return */
import {
  DeleteOutlined,
  EyeOutlined, ImportOutlined, LoadingOutlined, PlusOutlined,
  UploadOutlined
} from '@ant-design/icons';
import {
  Button,
  message,
  Modal,
  Space, Tooltip, Typography
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { AxiosError } from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppSelector } from 'app/store';
import HeaderPage from 'common/components/HeaderPage';
import PageTable from 'common/components/PageTable';
import {
  FilterDataProps,
  FilterValueProps,
  mappingFilterFields,
  mappingFilterToQuery,
  mappingParamsFilter,
  mappingQueryParamsFilter
} from 'common/components/PageTable/filter';
import PopupGuideSettingPopups from 'common/components/PopupGuideSettingPopups';
import { getLocalStorage, setLocalStorage } from 'common/services/common/storage';
import { getExportByCodeService } from 'common/services/extends/exports';
import {
  deleteStaffAssignService,
  exportStaffAssignService,
  getAllStaffAssignService,
  importStaffAssignTemplateService
} from 'common/services/extends/staffAssign';
import LOCAL_STORAGE, { NoTaxCode, ROUTE_PATHS } from 'common/utils/constant';
import {
  detectError, downloadFile, formatDateDDMMYYYY, isPopupBlocked
} from 'common/utils/functions';
import roles, { getPermission } from 'configs/roles';

export type TableDataTypes = {
  id: number;
  fullname: string;
  dob?: string;
  taxCode: string | null;
  position: string;
  phoneNumber: string;
  idCardNumber: string;
  idCardIssuedAt?: string;
  idCardIssuedBy?: string;
  permanentAddress: string;
  contractType?: OptionType;
};

const EmployeeAssignManagement: React.FC<ActiveRoles> = ({
  roleIndex, roleDelete, roleCreate, roleOther
}) => {
  /* Hooks */
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  /* Selectors */
  const {
    defaultPageSize, advancedFilter
  } = useAppSelector((state) => state.system);

  const rolesUser = useAppSelector((state) => state.auth.roles);

  /* Search Params */
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => {
    const paramsObj = { ...Object.fromEntries(searchParams.entries()) };
    if (searchParams.has('page')) delete paramsObj.page;
    return paramsObj;
  }, [searchParams]);
  const pageParam = searchParams.get('page');

  /* States */
  const [currentPage, setCurrentPage] = useState(Number(pageParam));
  const [currentView, setCurrentView] = useState(defaultPageSize);
  const [keyword, setKeyword] = useState('');
  const [selectedFilterList, setSelectedFilterList] = useState<
    FilterValueProps[]>(mappingQueryParamsFilter(params));
  const [downloading, setDownloading] = React.useState(false);
  const [isPopupBlock, setIsPopupBlock] = React.useState({
    status: false,
    link: ''
  });
  const [checkDownload, setCheckDownload] = React.useState({
    status: false,
    code: ''
  });

  /* ref */
  const clickedDownloadRef = React.useRef<boolean>(false);
  const checkStatusExportRef = React.useRef<boolean>(false);
  const isFirstRender = React.useRef(true);

  const returnParamsFilter = useMemo(
    () => mappingParamsFilter(selectedFilterList),
    [selectedFilterList]
  );

  /* Queries */
  const { data: employeeRes, isLoading } = useQuery(
    ['get-employee-assign-list', currentPage, currentView, selectedFilterList, keyword],
    () => getAllStaffAssignService({
      page: currentPage,
      limit: currentView,
      ...(keyword && { keyword }),
      ...returnParamsFilter
    }),
    {
      enabled: currentPage > 0,
      onError: (error: unknown) => {
        if (error instanceof AxiosError) {
          const status = error.response?.status;
          message.error(t(detectError(status)));
        }
      }
    }
  );

  const { mutate: deleteMutate, isLoading: deleteLoading } = useMutation(
    ['delete-employees-assign'],
    deleteStaffAssignService,
    {
      onSuccess: () => {
        // Invalidate the query to refetch the data
        queryClient.invalidateQueries(['get-employee-assign-list']);
        message.success(t('message.deleteSuccess'));
      },
      onError: () => {
        message.error(t('message.deleteError'));
      }
    }
  );

  const { mutate: importStaffAssignTemplateMutate, isLoading: loadingTemplate } = useMutation(
    ['imporStaffAssignTemplate'],
    () => importStaffAssignTemplateService(),
    {
      onSuccess: (data) => {
        if (data?.link) {
          window.open(data.link);
          message.success(t('message.exportSuccess'));
        }
      },
      onError: () => {
        message.error(t('message.exportError'));
      }
    }
  );

  const handleDelete = (ids: number[]) => {
    deleteMutate(ids);
  };

  const {
    mutate: exportStaffAssignMutate,
  } = useMutation(
    ['export-employee-assign'],
    exportStaffAssignService,
    {
      onSuccess: async (data: any) => {
        if (data?.downloadUrl) {
          const blocked = getLocalStorage(LOCAL_STORAGE.BLOCKED_POPUPS);
          if (blocked) {
            setIsPopupBlock({
              status: true,
              link: data.downloadUrl
            });
            return;
          }
          message.success(t('message.exportSuccess'));
          setCheckDownload({
            status: false,
            code: ''
          });
          window.open(data.downloadUrl, '_blank');
        } else {
          setCheckDownload({
            status: true,
            code: data.code || ''
          });
        }
      },
      onError: () => {
        message.error(t('message.exportError'));
      }
    }
  );
  /* Datas */

  const tableData = useMemo(() => employeeRes?.data.map((item) => ({
    ...item.staffAssignData,
  })) || [], [employeeRes]);

  /* Variables */
  const columns: ColumnsType<TableDataTypes> = [
    {
      title: t('assignmentStaff.fullname'),
      dataIndex: 'name',
      key: 'name',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.fullname}
        </Typography.Text>
      ),
    },
    {
      title: t('employee.taxNumber'),
      dataIndex: 'taxCode',
      key: 'taxCode',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.taxCode || NoTaxCode.label}
        </Typography.Text>
      ),
    },
    {
      title: t('system.position'),
      dataIndex: 'position',
      key: 'position',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.position}
        </Typography.Text>
      ),
    },
    {
      title: t('system.phone'),
      dataIndex: 'phone',
      key: 'phone',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.phoneNumber}
        </Typography.Text>
      ),
    },
    {
      title: t('employee.contractType'),
      dataIndex: 'contractType',
      key: 'contractType',
      render: () => (
        <Typography.Text>
          HDGV
        </Typography.Text>
      ),
    },
    {
      title: t('employee.identityNumber'),
      dataIndex: 'identityNumber',
      key: 'identityNumber',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.idCardNumber}
        </Typography.Text>
      ),
    },
    {
      title: t('employee.grantDate'),
      dataIndex: 'grantDate',
      key: 'grantDate',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {formatDateDDMMYYYY(data.idCardIssuedAt, '/')}
        </Typography.Text>
      ),
    },
    {
      title: t('employee.grantPlace'),
      dataIndex: 'grantPlace',
      key: 'grantPlace',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.idCardIssuedBy}
        </Typography.Text>
      ),
    },
    {
      title: t('system.action'),
      key: 'action',
      width: 100,
      align: 'center',
      fixed: 'right',
      render: (_: string, _data: TableDataTypes) => (
        <Space>
          <Tooltip title={t('system.viewDetail')}>
            <Button
              disabled={!roleIndex}
              icon={<EyeOutlined />}
              onClick={() => navigate(`${ROUTE_PATHS.EMPLOYEE_ASSIGN_DETAIL}?id=${_data.id}`)}
            />
          </Tooltip>
          <Tooltip title={t('system.delete')}>
            <Button
              disabled={!roleDelete}
              icon={<DeleteOutlined />}
              onClick={() => {
                Modal.confirm({
                  className: 't-pagetable_deleteRecordModal',
                  autoFocusButton: 'cancel',
                  okText: t('system.ok'),
                  cancelText: t('system.cancel'),
                  cancelButtonProps: {
                    type: 'primary',
                  },
                  okButtonProps: {
                    type: 'default',
                  },
                  title: t('message.confirmDeleteRecord'),
                  onOk: () => {
                    handleDelete([_data.id]);
                  },
                });
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleSearch = (val: string) => {
    setKeyword(val);
  };

  const handleSetCurrentPage = (page: number) => {
    setCurrentPage(page);
    setSearchParams({
      ...mappingFilterToQuery(selectedFilterList), page: page.toString()
    });
  };

  const handleSetCurrentView = (view: number) => {
    setCurrentView(view);
  };

  const handleFilter = (data: FilterValueProps) => {
    const typeFilter = String(data.filter).split('.')[1];
    if ((typeFilter === 'isNull' || typeFilter === 'isNotNull') && selectedFilterList.find((item) => item.key === data.key)) {
      return;
    }
    const counter = selectedFilterList.filter(
      (item) => item.field === data.field && item.filter === data.filter
    ).length;
    setSelectedFilterList([...selectedFilterList, { ...data, index: counter }]);
    handleSetCurrentPage(1);
  };

  const handleDeleteFilter = (key: string, index?: number) => {
    const tempList = selectedFilterList.slice();
    setSelectedFilterList(tempList.filter((item) => !(item.key === key && item.index === index)));
    handleSetCurrentPage(1);
  };

  const filterFields: FilterDataProps[] = useMemo(
    () => mappingFilterFields('staffAssign', advancedFilter),
    [advancedFilter]
  );

  const handleDownloadStaffAssignReport = async () => {
    try {
      if (clickedDownloadRef.current) {
        return;
      }
      clickedDownloadRef.current = true;

      setDownloading(true);
      setCheckDownload({
        status: false,
        code: ''
      });

      exportStaffAssignMutate({
        ...returnParamsFilter
      });
    } catch (error) {
      message.error(t('message.exportError'));
      setDownloading(false);
      clickedDownloadRef.current = false;
      setCheckDownload({
        status: false,
        code: ''
      });
      setIsPopupBlock({
        status: false,
        link: ''
      });
    }
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setCurrentPage(1);
    setSearchParams({
      ...mappingFilterToQuery(selectedFilterList),
      page: '1'
    }, { replace: true });
  }, [keyword, setSearchParams, selectedFilterList]);

  useEffect(() => {
    const blocked = isPopupBlocked('');
    if (blocked) {
      setLocalStorage(LOCAL_STORAGE.BLOCKED_POPUPS, 'true');
    } else {
      window.localStorage.removeItem(LOCAL_STORAGE.BLOCKED_POPUPS);
    }
  }, []);

  useEffect(() => {
    if (pageParam) {
      setCurrentPage(Number(pageParam));
    } else {
      setCurrentPage(1);
    }
  }, [pageParam]);

  useEffect(() => {
    if (checkDownload.status && checkDownload.code) {
      let numberOfChecks = 0;
      const interval = setInterval(async () => {
        if (checkStatusExportRef.current) {
          console.log('Already checking status, skipping this interval.');
          return;
        }

        // Limit the number of checks to prevent infinite loops
        if (numberOfChecks >= 9) {
          clearInterval(interval);
          message.error(t('message.exportError'));
          setCheckDownload({
            status: false,
            code: ''
          });
          setDownloading(false);
          clickedDownloadRef.current = false;
          return;
        }
        console.log('Checking download status...');
        try {
          checkStatusExportRef.current = true;
          numberOfChecks += 1;
          const response = await getExportByCodeService(checkDownload.code);
          if (response?.status === 'failed') {
            clearInterval(interval);
            message.error(t('message.exportError'));
            setCheckDownload({
              status: false,
              code: ''
            });
            setIsPopupBlock({
              status: false,
              link: ''
            });
            setDownloading(false);
            clickedDownloadRef.current = false;
            return;
          }
          if (response?.downloadUrl && response.status === 'success') {
            const blocked = getLocalStorage(LOCAL_STORAGE.BLOCKED_POPUPS);
            if (blocked && response) {
              setIsPopupBlock({
                status: true,
                link: response?.downloadUrl
              });
              clearInterval(interval);
              setCheckDownload({
                status: false,
                code: ''
              });
              setDownloading(false);
              clickedDownloadRef.current = false;
              return;
            }
            clearInterval(interval);
            // window.open(response.downloadUrl, '_blank');
            downloadFile(response.downloadUrl, 'danh-sach-nhan-vien-giao-viec');
            setCheckDownload({
              status: false,
              code: ''
            });
            setDownloading(false);
            clickedDownloadRef.current = false;
          } else {
            console.log('Download not ready yet, checking again...');
          }
        } catch (error) {
          clearInterval(interval);
          setIsPopupBlock({
            status: false,
            link: ''
          });
          console.error('Error checking download status:', error);
          message.error(t('message.exportError'));
          setCheckDownload({
            status: false,
            code: ''
          });
          setDownloading(false);
          clickedDownloadRef.current = false;
        } finally {
          checkStatusExportRef.current = false;
        }
      }, 3000); // Check every 3 seconds

      return () => clearInterval(interval); // Clear interval on unmount
    }
  }, [checkDownload, t]);

  /* Render */
  return (
    <>
      <HeaderPage
        fixed
        title={t('sidebar.employeeAssign')}
        rightHeader={(
          <Space>
            <Button
              disabled={!getPermission(roleOther, roles.EXPORT_STAFF_ASSIGN)}
              onClick={handleDownloadStaffAssignReport}
            >
              {
                downloading ? (
                  <Space>
                    {t('exportReports.checkingDownload')}
                    <LoadingOutlined style={{ fontSize: 16 }} />
                  </Space>
                ) : t('exportReports.export')
              }
            </Button>
            <Button
              type="dashed"
              disabled={loadingTemplate || !getPermission(rolesUser, roles.EMPLOYEE_ASSIGN_IMPORT)}
              onClick={() => importStaffAssignTemplateMutate()}
              loading={loadingTemplate}
            >
              <ImportOutlined />
              {t('system.downloadSampleFile')}
            </Button>
            <Button
              type="default"
              disabled={!getPermission(rolesUser, roles.EMPLOYEE_ASSIGN_IMPORT)}
              onClick={() => navigate(ROUTE_PATHS.EMPLOYEE_ASSIGN_IMPORT)}
            >
              <UploadOutlined />
              {t('employee.employeeAssignImport')}
            </Button>
            <Button
              type="primary"
              disabled={!roleCreate}
              onClick={() => navigate(ROUTE_PATHS.EMPLOYEE_ASSIGN_DETAIL)}
            >
              <PlusOutlined />
              {t('system.create')}
            </Button>
          </Space>
        )}
      />
      <div className="t-mainlayout_wrapper">
        <PageTable
          noCheckbox
          isLoading={isLoading || deleteLoading}
          handleSearch={handleSearch}
          filtersDataTable={{
            handleFilter,
            selectedFilterList,
            handleDeleteFilter,
          }}
          tableProps={{
            initShowColumns: ['name', 'taxCode', 'position', 'phone', 'contractType', 'identityNumber', 'grantDate', 'grantPlace', 'action'],
            columns,
            pageData: tableData,
            currentPage,
            pageSize: currentView,
            handleSetCurrentPage,
            handleSetCurrentView,
            total: employeeRes?.meta.total || 0,
            noBaseCol: true,
            noDeleteLanguage: true,
            filterFields
          }}
        />
      </div>
      <PopupGuideSettingPopups
        fileName="danh-sach-nhan-vien-giao-viec"
        isLoading={downloading}
        isPopupBlock={isPopupBlock}
        onClose={() => setIsPopupBlock({ status: false, link: '' })}
      />
    </>
  );
};

export default EmployeeAssignManagement;
